import ScheduleSendIcon from '@mui/icons-material/ScheduleSend';
import { LoadingButton } from '@mui/lab';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogContentText,
  Grid,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import PropTypes from 'prop-types';
import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { addReportSchedule } from '../../../../api/reports/index';
import {
  fetchReportOptions,
  updateActiveStep,
  updateFormValues,
  updateSelectedReportId,
} from '../../../../redux/slices/reports';
import Iconify from '../../../../reusable-components/iconify';
import { useSnackbar } from '../../../../reusable-components/snackbar';
import ReportScheduleStep from './ReportScheduleStep';
import ReportSelectSpecificationsStep from '../ReportSelectSpecificationsStep';
import ReportSelectTypeStep from '../ReportSelectTypeStep';
import { getEmptyFields } from '../ReportViewHelper';
import ReportIsDownloadingCard from '../results/ReportIsDownloadingCard';

ReportsScheduleModal.propTypes = {
  open: PropTypes.bool,
  edit: PropTypes.bool,
  handleClose: PropTypes.func,
  refetchSchedules: PropTypes.func,
  row: PropTypes.object,
  getReportResults: PropTypes.func,
};

export default function ReportsScheduleModal({ open, handleClose, refetchSchedules, row, getReportResults, edit }) {
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const {
    loadingMainData,
    loadingReportSpecificationOptions,
    loadingReportResults,
    downloadingReportResults,
    activeStep,
    formValues,
    selectedReportId,
    reportSpecificationOptions,
    createReportData,
    reportRecipients,
  } = useSelector((state) => state.reports?.data);
  const canScheduleSelectedReport = selectedReportId !== 16 && formValues.selectedReport?.hasAutomatePerm === true;

  const [showDatePicker, setShowDatePicker] = useState(false);
  const [toggleStep, setToggleStep] = useState('');
  const [value, setValue] = useState({
    scheduleId: '',
    name: '',
    frequency: '',
    timeFrame: '',
    recipients: [],
    ftpSiteId: '',
  });

  useEffect(() => {
    if (edit) setToggleStep('2');
  }, [edit]);

  useEffect(() => {
    if (activeStep === 1 && !edit) setToggleStep('1');
  }, [activeStep, edit]);

  useEffect(() => {
    if (open && row) {
      const distinctNames = [...new Set(row.listScheduleOptions.map((item) => item.name))];
      const scheduleOptionsByName = {};
      distinctNames.forEach((propertyName) => {
        scheduleOptionsByName[`${propertyName?.slice(0, 1)?.toLowerCase()}${propertyName?.slice(1)}`] =
          row.listScheduleOptions
            .filter((s) => s.name === propertyName)
            .map((o) => {
              const selectedOptions =
                createReportData[`${propertyName?.slice(0, 1)?.toLowerCase()}${propertyName?.slice(1)}`]?.find(
                  (a) => a.id === o.optionId || a?.value === o.optionId
                ) ?? `${o.optionId}`;
              return selectedOptions;
            });
      });
      scheduleOptionsByName.useBudgetDate = scheduleOptionsByName.useBudgetDate
        ? scheduleOptionsByName.useBudgetDate[0] === '1'
        : false;
      if (scheduleOptionsByName.skip) scheduleOptionsByName.skip = scheduleOptionsByName.skip[0];
      if (scheduleOptionsByName.take) scheduleOptionsByName.take = scheduleOptionsByName.take[0];
      scheduleOptionsByName.pendingOnly = scheduleOptionsByName.pendingOnly
        ? scheduleOptionsByName.pendingOnly[0] === '1'
        : false;
      scheduleOptionsByName.includeChildFacs = scheduleOptionsByName.includeChildFacs
        ? scheduleOptionsByName.includeChildFacs[0] === '1'
        : false;
      scheduleOptionsByName.includeFacsWithoutGroup = scheduleOptionsByName.includeFacsWithoutGroup
        ? scheduleOptionsByName.includeFacsWithoutGroup[0] === '1'
        : false;
      scheduleOptionsByName.excludeTicketData = scheduleOptionsByName.excludeTicketData
        ? scheduleOptionsByName.excludeTicketData[0] === '1'
        : false;

      const recipientMap = Object.fromEntries((row?.recipients || []).map((r) => [r.label, r]));

      const userRecipients = reportRecipients
        .filter((user) => recipientMap[user.email])
        .map((user) => ({
          label: `${user.firstName} ${user.lastName}`,
          email: user.email,
          value: recipientMap[user.email]?.value,
        }));

      const nonUserRecipients = (row?.recipients || [])
        .filter((r) => !reportRecipients.some((user) => user.email === r.label))
        .map((r) => ({
          label: r.label,
          email: r.label,
          value: r.value,
        }));

      const matchedUsers = [...userRecipients, ...nonUserRecipients];

      const report = createReportData.reports.find((r) => r.id === row.reportId);
      const reportType = createReportData.reportTypes.find((r) => r.id === report.typeId);

      dispatch(updateSelectedReportId(report.id));
      dispatch(fetchReportOptions(report.id));

      dispatch(
        updateFormValues({
          ...formValues,
          selectedReportType: reportType,
          selectedReport: report,
          startDate: null,
          endDate: null,
          name: row.name,
          active: row.active,
          ftpSiteId: row.ftpSiteId,
          downloadAsCsv: row.downloadAsCsv ?? false,
          recipients: matchedUsers,
          frequency: { id: row.frequencyId, name: row.frequency },
          timeFrame: { id: row.dateFilterId, name: row.dateFilter },
          serialNumbers: [],
          ...scheduleOptionsByName,
        })
      );
    }
  }, [row, open]);

  const startStepReportSpecificationOptions = () => {
    try {
      dispatch(fetchReportOptions(selectedReportId));
    } catch (error) {
      enqueueSnackbar(`Failed to fetch report specifications`, { variant: 'error' });
    }
  };

  const scheduleReport = async () => {
    const arr = [];
    reportSpecificationOptions.forEach((o) => {
      const values =
        formValues[`${o.name[0]?.toLowerCase()}${o.name?.slice(1)}`] ??
        formValues[`${o.name[0]?.toLowerCase()}${o.name?.slice(1)}s`];
      if (Array.isArray(values)) {
        values.forEach((v) => arr.push(v.value || v.id ? { key: o.id, value: v.value || v.id } : v));
      } else if (typeof values === 'boolean') {
        arr.push({ key: o.id, value: values === true ? 1 : 0 });
      } else if (values && typeof values === 'object') {
        arr.push({ key: o.id, value: values?.value });
      } else {
        arr.push({ key: o.id, value: values });
      }
    });
    try {
      const result = await addReportSchedule(formValues, arr, row?.id);
      if (!result?.success) {
        enqueueSnackbar(result?.message ?? 'Failed to save report schedule', { variant: 'error' });
        return;
      }
      refetchSchedules();
      handleClose();
    } catch (error) {
      enqueueSnackbar(error?.message ?? 'Failed to save report schedule', { variant: 'error' });
    }
  };

  useEffect(() => {
    if (activeStep === 2) handleClose(false);
  }, [activeStep]);

  const hasEmptyField = useMemo(
    () => getEmptyFields(reportSpecificationOptions, formValues, selectedReportId, toggleStep === '2'),
    [reportSpecificationOptions, formValues, toggleStep]
  );

  return (
    <Dialog
      open={open}
      onClose={(event, reason) => {
        if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;
        handleClose();
      }}
      maxWidth={showDatePicker && activeStep === 1 ? 'lg' : 'sm'}
    >
      <Box sx={{ width: '100%', height: '100%', justifyContent: 'center', display: 'flex', alignItems: 'center' }}>
        {activeStep !== 2 && (
          <Box
            sx={{
              width: showDatePicker && activeStep === 1 ? '950px' : '600px',
              height: '100%',
              '& .MuiBackdrop-root': { backgroundColor: 'transparent' },
            }}
          >
            <Typography component="div" sx={{ display: 'flex', color: '#8cc53f', mb: -3, mt: 2 }}>
              <Button
                title="Back"
                onClick={() => dispatch(updateActiveStep(activeStep - 1))}
                style={{ color: 'gray', maxWidth: '20' }}
                disabled={activeStep === 0}
                sx={{ visibility: activeStep === 0 ? 'hidden' : 'visible' }}
              >
                <Iconify icon="ic:baseline-arrow-back" width={20} height={20} />
              </Button>

              <Box sx={{ flex: 1, textAlign: 'center', fontWeight: 'bold', fontSize: '20px' }}>
                {activeStep === 0 && 'New Report'}
                {activeStep > 0 && formValues.selectedReport?.name}
              </Box>

              <Button
                title="Close"
                onClick={() => {
                  handleClose(false);
                  setToggleStep('0');
                }}
                style={{ color: 'gray', maxWidth: '20' }}
              >
                <Iconify icon="ic:baseline-close" width={20} height={20} />
              </Button>
            </Typography>

            <DialogContent>
              {!loadingMainData && !loadingReportSpecificationOptions ? (
                <>
                  {activeStep === 0 && <ReportSelectTypeStep />}
                  {activeStep === 1 && (
                    <Box display="flex" justifyContent="center" mb={1}>
                      <ToggleButtonGroup
                        value={toggleStep}
                        exclusive
                        onChange={(_, tab) => setToggleStep(tab)}
                        sx={{ m: 4, mb: 1 }}
                      >
                        <ToggleButton value="1">Generate Report</ToggleButton>
                        {canScheduleSelectedReport && <ToggleButton value="2">Schedule Report</ToggleButton>}
                      </ToggleButtonGroup>
                    </Box>
                  )}
                  {activeStep === 1 && toggleStep === '2' && canScheduleSelectedReport && (
                    <>
                      <DialogContentText sx={{ fontWeight: 'bold' }}>
                        Scheduled Report Name{' '}
                        <Tooltip title="Add a descriptive title here for the scheduled report. Example: Nancy's weekly Budget report for Ridge Haven.">
                          <Iconify icon="material-symbols:info-outline" width={14} height={14} color="info" />
                        </Tooltip>
                      </DialogContentText>
                      <TextField
                        xs={12}
                        size="small"
                        variant="outlined"
                        fullWidth
                        value={formValues?.name ?? ''}
                        onChange={(event) => dispatch(updateFormValues({ ...formValues, name: event.target.value }))}
                        autoComplete="off"
                      />
                    </>
                  )}
                  {activeStep === 1 && (toggleStep === '1' || toggleStep === '2') && (
                    <ReportSelectSpecificationsStep
                      showDatePicker={showDatePicker}
                      setShowDatePicker={setShowDatePicker}
                      toggleStep={toggleStep}
                      isScheduleReport={toggleStep === '2'}
                    />
                  )}
                  {activeStep === 1 && toggleStep === '2' && canScheduleSelectedReport && (
                    <ReportScheduleStep row={row} value={value} setValue={setValue} />
                  )}

                  <Grid container direction="row" justifyContent="center" alignItems="flex-end" spacing={2}>
                    {activeStep === 0 && (
                      <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'center' }}>
                        <Button
                          sx={{ width: '50%', mt: 2 }}
                          title="Next"
                          variant="contained"
                          disabled={!formValues.selectedReport}
                          onClick={startStepReportSpecificationOptions}
                        >
                          Next
                        </Button>
                      </Grid>
                    )}
                    {activeStep === 1 && toggleStep === '2' && canScheduleSelectedReport && (
                      <Grid item xs={6}>
                        <Tooltip title={hasEmptyField.length > 0 ? `Missing ${hasEmptyField.join(', ')}` : ''}>
                          <span>
                            <LoadingButton
                              sx={{ width: '100%', mt: 2 }}
                              title="Save"
                              variant={activeStep === 1 ? 'outlined' : 'contained'}
                              loading={loadingReportResults}
                              endIcon={<ScheduleSendIcon />}
                              onClick={() => scheduleReport()}
                              disabled={hasEmptyField.length > 0}
                            >
                              Save schedule
                            </LoadingButton>
                          </span>
                        </Tooltip>
                      </Grid>
                    )}
                    {downloadingReportResults && activeStep === 1 && <ReportIsDownloadingCard />}
                    {activeStep === 1 && !downloadingReportResults && toggleStep === '1' && (
                      <Grid item xs={6}>
                        <Tooltip title={hasEmptyField.length > 0 ? `Missing ${hasEmptyField.join(', ')}` : ''}>
                          <span>
                            <LoadingButton
                              sx={{ width: '100%', mt: 2 }}
                              title="Generate Report"
                              variant="contained"
                              loading={loadingReportResults || downloadingReportResults}
                              onClick={() => getReportResults(selectedReportId === 16 || selectedReportId === 20)}
                              // ^ Report 16 jumps directly to download, otherwise we get the data table first
                              endIcon={<Iconify icon={'tabler:send'} />}
                              disabled={hasEmptyField.length > 0}
                            >
                              Generate Report
                            </LoadingButton>
                          </span>
                        </Tooltip>
                      </Grid>
                    )}
                  </Grid>
                </>
              ) : (
                <Box justifyContent="center" display="flex">
                  <CircularProgress sx={{ alignSelf: 'center', margin: '50px' }} />
                </Box>
              )}
            </DialogContent>
          </Box>
        )}
      </Box>
    </Dialog>
  );
}
